import React, { useState, useEffect } from 'react';
import './PurchaseForm.css';
import api from '../../../services/publicApi';
import { getSalesChannel } from '../../../services/telegramWebApp';

const createCheckoutKey = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const PurchaseForm = ({ product, variant, onBack }) => {
    const [formData, setFormData] = useState({
        full_name: '',
        phone: '',
        email: '',
        delivery_address: '',
        telegram: '',
        promocode: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [promoInfo, setPromoInfo] = useState(null);
    const [checkingPromo, setCheckingPromo] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [checkoutKey] = useState(createCheckoutKey);

    const productName = product?.name || 'Без названия';
    const productColor = product?.color_name || '';
    const productPrice = variant?.price || product?.price || 0;
    const selectedSize = variant?.size || '';

    const discount = promoInfo?.valid ? (productPrice * promoInfo.discount_percent) / 100 : 0;
    const finalPrice = productPrice - discount;

    useEffect(() => {
        if (!formData.promocode.trim()) {
            setPromoInfo(null);
            return;
        }

        const timer = setTimeout(async () => {
            setCheckingPromo(true);
            try {
                const result = await api.promo_codes.check(formData.promocode.trim());
                setPromoInfo(result);
            } catch {
                setPromoInfo(null);
            } finally {
                setCheckingPromo(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [formData.promocode]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (!variant || variant.available_quantity <= 0) {
                throw new Error('Выбранный размер недоступен');
            }

            const body = {
                variant_id: variant.id,
                quantity: 1,
                idempotency_key: checkoutKey,
                sales_channel: getSalesChannel(),
                phone_number: formData.phone,
                email: formData.email || null,
                full_name: formData.full_name,
                delivery_address: formData.delivery_address,
                telegram: formData.telegram || null,
            };

            if (promoInfo?.valid) {
                body.promocode = formData.promocode.trim();
            }

            const checkout = await api.checkout.create(body);
            window.location.assign(checkout.confirmation_url);
        } catch (err) {
            const message = err.message === 'out_of_stock'
                ? 'Этот размер только что закончился. Вернитесь и выберите другой размер.'
                : (err.message || 'Ошибка оформления заказа');
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="shop-purchase-container">
            <button className="shop-back-btn" onClick={onBack}>← Назад к товару</button>
            <h2>Оформление заказа</h2>

            <div className="shop-order-summary">
                <p><strong>Товар:</strong> {productName}</p>
                {productColor && <p><strong>Цвет:</strong> {productColor}</p>}
                <p><strong>Размер:</strong> {selectedSize}</p>
                {promoInfo?.valid ? (
                    <>
                        <p className="shop-price-old">{new Intl.NumberFormat('ru-RU').format(productPrice)} ₽</p>
                        <p className="shop-price-new"><strong>Сумма со скидкой:</strong> {new Intl.NumberFormat('ru-RU').format(finalPrice)} ₽</p>
                        <p className="shop-promo-info">Промокод <strong>{promoInfo.code}</strong> — скидка {promoInfo.discount_percent}%</p>
                    </>
                ) : (
                    <p><strong>Сумма:</strong> {new Intl.NumberFormat('ru-RU').format(productPrice)} ₽</p>
                )}
            </div>

            {error && <div className="shop-error-message">{error}</div>}

            <form onSubmit={handleSubmit} className="shop-purchase-form">
                <div className="shop-form-group">
                    <label htmlFor="shop_full_name">ФИО *</label>
                    <input type="text" id="shop_full_name" name="full_name" value={formData.full_name} onChange={handleChange} required placeholder="Иванов Иван Иванович" />
                </div>
                <div className="shop-form-group">
                    <label htmlFor="shop_phone">Телефон *</label>
                    <input type="tel" id="shop_phone" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+7 (999) 999-99-99" />
                </div>
                <div className="shop-form-group">
                    <label htmlFor="shop_email">Email</label>
                    <input type="email" id="shop_email" name="email" value={formData.email} onChange={handleChange} placeholder="example@mail.ru" />
                </div>
                <div className="shop-form-group">
                    <label htmlFor="shop_address">Адрес доставки *</label>
                    <textarea id="shop_address" name="delivery_address" value={formData.delivery_address} onChange={handleChange} required placeholder="Город, улица, дом, квартира" rows="3" />
                </div>
                <div className="shop-form-group">
                    <label htmlFor="shop_telegram">Telegram (необязательно)</label>
                    <input
                        type="text"
                        id="shop_telegram"
                        name="telegram"
                        value={formData.telegram}
                        onChange={handleChange}
                        placeholder="@username, если удобнее связаться там"
                    />
                </div>
                <div className="shop-form-group">
                    <label htmlFor="shop_promocode">Промокод</label>
                    <input type="text" id="shop_promocode" name="promocode" value={formData.promocode} onChange={handleChange} placeholder="Введите промокод (если есть)" />
                    {checkingPromo && <span className="shop-promo-checking">Проверка...</span>}
                    {promoInfo && !promoInfo.valid && <span className="shop-promo-invalid">{promoInfo.message}</span>}
                </div>

               <label className="shop-agreement">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                    <span>
                        Я согласен на{' '}
                        <a href="https://disk.yandex.ru/i/bPpdOnaP6_Ywkw" target="_blank" rel="noopener noreferrer">
                            обработку персональных данных
                        </a>
                    </span>
                </label>
                <button type="submit" className="shop-submit-btn" disabled={loading || !agreed}>
                    {loading ? 'Создание заказа...' : `Перейти к оплате${promoInfo?.valid ? ` (${new Intl.NumberFormat('ru-RU').format(finalPrice)} ₽)` : ''}`}
                </button>
            </form>
        </div>
    );
};

export default PurchaseForm;
